import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { usePaginatedFetch } from '../shared/usePaginatedFetch';

const SchoolContext = createContext();

export const useSchool = () => useContext(SchoolContext);

export const SchoolProvider = ({ children }) => {
    // --- State Management ---
    const [selectedBranch, setSelectedBranch] = useState('All Branches');
    const [selectedDate, setSelectedDate] = useState({
        month: new Date().getMonth(),
        year: new Date().getFullYear()
    });

    // Static Entities (Tier A)
    const [packages, setPackages] = useState([]);
    const [promotions, setPromotions] = useState([]);
    const [branches, setBranches] = useState([]);
    const [staff, setStaff] = useState([]);
    const [instructors, setInstructors] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [settings, setSettings] = useState([]);
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [pendingUsers, setPendingUsers] = useState([]);

    const [loadingStatic, setLoadingStatic] = useState(true);
    const [error, setError] = useState(null);

    const config = window.reactPress || window.rpVars || {};
    const API_BASE = (config.api?.rest_url || '/wp-json').replace(/\/$/, '') + '/sdt/v1';
    const nonce = config.api?.nonce || config.nonce;

    const headers = {
        'Content-Type': 'application/json',
        ...(nonce && { 'X-WP-Nonce': nonce })
    };

    // --- Mappers ---
    const mapLead = (l) => {
        if (!l) return null;
        return {
            ...l,
            id: l.id || l.lead_id || Math.random(),
            name: (l.first_name && l.last_name) ? `${l.first_name} ${l.last_name}` : (l.name || 'Unknown User'),
            phone: l.phone_number || l.phone || '',
            email: l.email || '',
            source: l.lead_source || l.source || 'Other',
            branch: l.branch || 'Harare',
            interest: l.interest,
            status: l.status || 'New',
            notes: l.notes || '',
            date: l.date || l.created_at || new Date().toISOString()
        };
    };

    const mapCustomer = (c) => {
        if (!c) return null;
        let docs = [];
        try {
            docs = c.documents ? JSON.parse(c.documents) : [];
        } catch (e) {
            docs = c.documents ? c.documents.split(',') : [];
        }
        return {
            ...c,
            id: c.id,
            name: c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Unknown Student',
            instructorId: c.instructor_id,
            branchId: c.branch_id,
            userId: c.user_id,
            documents: Array.isArray(docs) ? docs : [],
            phone: c.phone_number || c.phone,
            email: c.email_address || c.email,
            stage: c.stage || 'New',
            balance: parseFloat(c.balance || 0),
            branch: c.branch || 'Main',
            nextOfKin: {
                name: c.next_of_kin_name || '',
                relationship: c.relationship_with_next_of_kin || '',
                phone: c.next_of_kin_phone_number || ''
            },
            lessonsRemaining: c.lessons_remaining,
            enrollmentDate: c.enrollment_date,
            dateOfBirth: c.date_of_birth,
            notes: c.notes || ''
        };
    };

    const mapPackage = (p) => {
        if (!p) return null;
        return {
            ...p,
            id: p.id,
            name: p.package_name || p.name,
            price: parseFloat(p.customer_price || p.price || 0),
            costPrice: parseFloat(p.package_cost_price || p.cost_price || 0),
            lessonsCount: parseInt(p.lesson_count || p.lessons_count || 0),
            description: p.package_details || p.description
        };
    };

    const mapPromotion = (p) => {
        if (!p) return null;
        return {
            ...p,
            id: p.id,
            name: p.promotion_name || p.name,
            discountValue: parseFloat(p.discount_value || 0),
            discountType: p.discount_type,
            packageId: p.package_id,
            startDate: p.promotion_start_date || p.start_date,
            endDate: p.promotion_end_date || p.end_date
        };
    };

    const mapStaff = (s) => {
        if (!s) return null;
        return {
            ...s,
            id: s.id,
            name: s.name || `${s.first_name || ''} ${s.last_name || ''}`.trim() || 'Unknown Staff',
            phone: s.phone_number || s.phone,
            email: s.email_address || s.email,
            role: s.assigned_role || s.role || 'Staff',
            status: s.status || 'Active',
            branchId: s.branch_id,
            userId: s.user_id,
            userLogin: s.user_login
        };
    };

    // --- Data Fetching ---
    const leadsData = usePaginatedFetch({ url: `${API_BASE}/leads`, headers, mapper: mapLead, perPage: 100 });
    const studentsData = usePaginatedFetch({ url: `${API_BASE}/customer`, headers, mapper: mapCustomer, perPage: 200 });
    const paymentsData = usePaginatedFetch({ url: `${API_BASE}/payments`, headers, perPage: 100 });
    const expensesData = usePaginatedFetch({ url: `${API_BASE}/expenses`, headers, perPage: 100 });
    const lessonsData = usePaginatedFetch({ url: `${API_BASE}/lessons`, headers, perPage: 100 });

    const fetchStaticData = useCallback(async () => {
        setLoadingStatic(true);
        try {
            const [
                pkgsRes, promosRes, branchesRes, staffRes, instRes, vehiclesRes, settingsRes, usersRes, rolesRes, pendingUsersRes
            ] = await Promise.all([
                fetch(`${API_BASE}/packages`, { headers }),
                fetch(`${API_BASE}/promotions`, { headers }),
                fetch(`${API_BASE}/branches`, { headers }),
                fetch(`${API_BASE}/staff`, { headers }),
                fetch(`${API_BASE}/instructor`, { headers }),
                fetch(`${API_BASE}/vehicles`, { headers }),
                fetch(`${API_BASE}/settings`, { headers }),
                fetch(`${API_BASE}/users`, { headers }),
                fetch(`${API_BASE}/users/roles`, { headers }),
                fetch(`${API_BASE}/users/pending`, { headers })
            ]);

            if (pkgsRes.ok) {
                const data = await pkgsRes.json();
                setPackages(data.map(mapPackage));
            }
            if (promosRes.ok) {
                const data = await promosRes.json();
                setPromotions(data.map(mapPromotion));
            }
            if (branchesRes.ok) setBranches(await branchesRes.json());
            if (staffRes.ok) {
                const data = await staffRes.json();
                setStaff(data.map(mapStaff));
            }
            if (instRes.ok) {
                const data = await instRes.json();
                setInstructors(data.map(mapStaff));
            }
            if (vehiclesRes.ok) setVehicles(await vehiclesRes.json());
            if (settingsRes.ok) setSettings(await settingsRes.json());
            if (usersRes.ok) setUsers(await usersRes.json());
            if (rolesRes && rolesRes.ok) setRoles(await rolesRes.json());
            if (pendingUsersRes && pendingUsersRes.ok) setPendingUsers(await pendingUsersRes.json());

        } catch (err) {
            console.error('Error fetching School static data:', err);
            setError('Failed to load static live data.');
        } finally {
            setLoadingStatic(false);
        }
    }, [nonce]);

    const fetchData = () => {
        fetchStaticData();
        leadsData.refresh();
        studentsData.refresh();
        paymentsData.refresh();
        expensesData.refresh();
        lessonsData.refresh();
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nonce]);

    const loading = loadingStatic || studentsData.loading || leadsData.loading;
    const students = studentsData.data;
    const leads = leadsData.data;
    const payments = paymentsData.data;
    const expenses = expensesData.data;
    const lessons = lessonsData.data;

    // --- Generic CRUD Helpers ---
    const createItem = async (endpoint, item, stateSetter, mapper = null) => {
        try {
            const res = await fetch(`${API_BASE}/${endpoint}`, {
                method: 'POST',
                headers,
                body: JSON.stringify(item)
            });
            if (!res.ok) throw new Error('Failed to create item');
            const newItem = await res.json();
            const listRes = await fetch(`${API_BASE}/${endpoint}`, { headers });
            if (listRes.ok) {
                const data = await listRes.json();
                stateSetter(mapper ? data.map(mapper) : data);
            }
            return newItem;
        } catch (e) {
            console.error(`Error creating ${endpoint}:`, e);
            throw e;
        }
    };

    const updateItem = async (endpoint, id, updates, stateSetter, mapper = null) => {
        try {
            const res = await fetch(`${API_BASE}/${endpoint}/${id}`, {
                method: 'POST',
                headers,
                body: JSON.stringify(updates)
            });
            if (!res.ok) throw new Error('Failed to update item');
            const listRes = await fetch(`${API_BASE}/${endpoint}`, { headers });
            if (listRes.ok) {
                const data = await listRes.json();
                stateSetter(mapper ? data.map(mapper) : data);
            }
        } catch (e) {
            console.error(`Error updating ${endpoint}:`, e);
            throw e;
        }
    };

    const deleteItem = async (endpoint, id, stateSetter) => {
        if (!window.confirm('Are you sure you want to delete this item?')) return;
        try {
            const res = await fetch(`${API_BASE}/${endpoint}/${id}`, {
                method: 'DELETE',
                headers
            });
            if (!res.ok) throw new Error('Failed to delete item');
            stateSetter(prev => prev.filter(item => item.id !== id));
        } catch (e) {
            console.error(`Error deleting ${endpoint}:`, e);
            throw e;
        }
    };

    // --- Specific CRUD Methods ---
    const addPackage = (pkg) => createItem('packages', pkg, setPackages, mapPackage);
    const updatePackage = (id, pkg) => updateItem('packages', id, pkg, setPackages, mapPackage);
    const deletePackage = (id) => deleteItem('packages', id, setPackages);

    const addPromotion = (promo) => createItem('promotions', promo, setPromotions, mapPromotion);
    const updatePromotion = (id, promo) => updateItem('promotions', id, promo, setPromotions, mapPromotion);
    const deletePromotion = (id) => deleteItem('promotions', id, setPromotions);

    const addBranch = (branch) => createItem('branches', branch, setBranches);
    const updateBranch = (id, branch) => updateItem('branches', id, branch, setBranches);
    const deleteBranch = (id) => deleteItem('branches', id, setBranches);

    const addStaff = (member) => createItem('staff', member, setStaff, mapStaff);
    const updateStaff = (id, member) => updateItem('staff', id, member, setStaff, mapStaff);
    const deleteStaff = (id) => deleteItem('staff', id, setStaff);

    const addInstructor = (member) => createItem('instructor', member, setInstructors, mapStaff);
    const updateInstructor = (id, member) => updateItem('instructor', id, member, setInstructors, mapStaff);
    const deleteInstructor = (id) => deleteItem('instructor', id, setInstructors);

    const addUser = (userData) => createItem('users', userData, setUsers);
    const updateUser = (id, userData) => updateItem('users', id, userData, setUsers);

    const approveUser = async (id) => {
        try {
            const res = await fetch(`${API_BASE}/users/${id}/approve`, { method: 'POST', headers });
            if (res.ok) { fetchData(); return true; }
        } catch (e) { console.error('Error approving user:', e); }
        return false;
    };

    const rejectUser = async (id) => {
        try {
            const res = await fetch(`${API_BASE}/users/${id}/reject`, { method: 'POST', headers });
            if (res.ok) { fetchData(); return true; }
        } catch (e) { console.error('Error rejecting user:', e); }
        return false;
    };

    const addVehicle = (v) => createItem('vehicles', v, setVehicles);
    const updateVehicle = (id, v) => updateItem('vehicles', id, v, setVehicles);
    const deleteVehicle = (id) => deleteItem('vehicles', id, setVehicles);

    const updateSetting = async (key, value) => {
        try {
            const res = await fetch(`${API_BASE}/settings`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ setting_key: key, setting_value: value })
            });
            if (res.ok) {
                const listRes = await fetch(`${API_BASE}/settings`, { headers });
                if (listRes.ok) setSettings(await listRes.json());
            }
        } catch (e) { console.error('Error updating setting:', e); }
    };

    const addLead = async (lead) => {
        try {
            const res = await fetch(`${API_BASE}/leads`, {
                method: 'POST',
                headers,
                body: JSON.stringify(lead)
            });
            if (!res.ok) throw new Error('Failed to create lead');
            const newLead = await res.json();
            leadsData.refresh();
            return newLead;
        } catch (e) {
            console.error('Error creating lead:', e);
            throw e;
        }
    };

    // --- Helpers ---
    const getBranchName = (branchId) => {
        if (!branchId) return '';
        const branch = branches.find(b => String(b.branch_id) === String(branchId) || String(b.id) === String(branchId));
        return branch ? (branch.branch_name || branch.name) : '';
    };

    const getBranchId = (branchName) => {
        if (!branchName) return null;
        const branch = branches.find(b =>
            (b.branch_name && b.branch_name.toLowerCase() === branchName.toLowerCase()) ||
            (b.name && b.name.toLowerCase() === branchName.toLowerCase())
        );
        return branch ? branch.branch_id : null;
    };

    // --- Computed Data (Original BO Charts) ---
    const safeStudents = students || [];
    const safeLeads = leads || [];
    const safePayments = payments || [];
    const safeExpenses = expenses || [];
    const safeBranches = branches || [];
    const safePackages = packages || [];

    const filteredStudents = selectedBranch === 'All Branches'
        ? safeStudents
        : safeStudents.filter(s => s.branch === selectedBranch || String(s.branch_id) === String(getBranchId(selectedBranch)));

    const filteredLeads = selectedBranch === 'All Branches'
        ? safeLeads
        : safeLeads.filter(l => l.branch === selectedBranch);

    const totalStudents = filteredStudents.length;
    const activePackages = filteredStudents.filter(s => ['active', 'Active'].includes(s.status)).length;

    const filteredPayments = selectedBranch === 'All Branches'
        ? safePayments
        : safePayments.filter(p => p.branch === selectedBranch);

    const monthlyIncome = filteredPayments.reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0);
    const runwayMonths = 4.5;

    const packageDistribution = safePackages.map(pkg => ({
        name: pkg.name,
        value: filteredStudents.filter(s => s.package_id === pkg.id || s.packageName === pkg.name).length
    }));

    const statusDistribution = ['Active', 'Watchlist', 'Inactive'].map(status => ({
        name: status,
        value: filteredStudents.filter(s => (s.status || '').toLowerCase() === status.toLowerCase()).length
    }));

    const stages = ['New', 'Provisional', 'Simulator', 'Road Lesson', 'Graduated'];
    const stageDistribution = stages.map(stage => ({
        name: stage,
        value: filteredStudents.filter(s => (s.stage || '').toLowerCase() === stage.toLowerCase()).length
    }));

    const generateFinancialChartData = useCallback(() => {
        const today = new Date();
        const allMonths = [];
        for (let i = -6; i < 6; i++) {
            const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
            allMonths.push(d);
        }

        const sumByMonth = (items, dateField, amountField, targetDate) => {
            return (items || []).filter(item => {
                if (!item[dateField]) return false;
                const itemDate = new Date(item[dateField]);
                return itemDate.getMonth() === targetDate.getMonth() &&
                    itemDate.getFullYear() === targetDate.getFullYear();
            }).reduce((sum, item) => sum + parseFloat(item[amountField] || 0), 0);
        };

        const fiveDaysAgo = new Date();
        fiveDaysAgo.setDate(today.getDate() - 5);
        const recentIncome = safePayments.filter(p => p.payment_date && new Date(p.payment_date) >= fiveDaysAgo);
        const recentExpenses = safeExpenses.filter(e => e.expense_date && new Date(e.expense_date) >= fiveDaysAgo);
        const avgDailyIncome = recentIncome.length ? (recentIncome.reduce((s, p) => s + parseFloat(p.amount || 0), 0) / 5) : 0;
        const avgDailyExpense = recentExpenses.length ? (recentExpenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0) / 5) : 0;

        return allMonths.map(date => {
            const isFuture = date > today;
            const monthLabel = date.toLocaleString('default', { month: 'short' });
            let income = 0;
            let expenditure = 0;
            let projection = null;

            if (isFuture) {
                projection = avgDailyIncome * 30;
            } else {
                income = sumByMonth(safePayments, 'payment_date', 'amount', date);
                expenditure = sumByMonth(safeExpenses, 'expense_date', 'amount', date);
            }

            return {
                month: monthLabel,
                income: isFuture ? null : income,
                expenditure: isFuture ? null : expenditure,
                projection: isFuture ? projection : null
            };
        });
    }, [safePayments, safeExpenses]);

    const lossMakingStudents = filteredStudents.filter(s => s.is_loss_making || s.isLossMaking);

    const branchPerformance = safeBranches.map(b => {
        const bName = b.branch_name || b.name || '';
        const bStudents = safeStudents.filter(s => s.branch === bName || String(s.branch_id) === String(b.branch_id));
        const bRevenue = safePayments.filter(p => p.branch === bName).reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0);
        return {
            branch: bName,
            students: bStudents.length,
            revenue: bRevenue,
            status: bRevenue > 5000 ? 'Healthy' : 'Stable'
        };
    });

    // --- Import Logic ---
    const handleTemplateImport = async (combinedData, onProgress) => {
        let imported = 0; let duplicates = 0; let errors = 0;
        const sheetMap = {
            'Leads': { endpoint: 'leads', mapper: (r) => ({ name: r.Name, phone_number: r.Phone, email: r.Email, source: r.Source, interest: r.Interest, branch: r.Branch || 'Harare', status: 'New', date: new Date().toISOString() }) },
            'Instructors': { endpoint: 'staff', mapper: (r) => ({ first_name: r.FirstName, surname: r.Surname, name: `${r.FirstName} ${r.Surname}`, phone: r.Phone, email: r.Email, national_id: r.NationalID, branch_id: r.Branch, role: 'Instructor', status: r['Status (Active/Inactive)'] || 'Active' }) },
            'Students': { endpoint: 'customer', mapper: (r) => ({ first_name: r.FirstName, last_name: r.Surname, name: `${r.FirstName} ${r.Surname}`, email: r.Email, phone: r.Phone, national_id: r.NationalID, address: r['Address (Residential)'], date_of_birth: r['DateOfBirth (YYYY-MM-DD)'], gender: r.Gender, package_id: r.Package, branch_id: r.Branch, status: 'Active' }) },
            'PaymentTypes': { endpoint: 'packages', mapper: (r) => ({ name: r.PackageName, price: parseFloat(r.Price), lessons_count: parseInt(r.LessonsCount), type: 'standard', duration: 'N/A' }) },
            'Vehicles': { endpoint: 'vehicles', mapper: (r) => ({ make: r.Make, model: r.Model, registration_number: r.RegistrationNumber, branch: r.Branch, status: r.Status || 'Available' }) },
            'Income': { endpoint: 'payments', mapper: (r) => ({ payment_date: r['Date (YYYY-MM-DD)'], amount: parseFloat(r.Amount), student_name: r['StudentName/ID'], method: r.PaymentMethod, description: r.Description }) },
            'Expenses': { endpoint: 'expenses', mapper: (r) => ({ expense_date: r['Date (YYYY-MM-DD)'], amount: parseFloat(r.Amount), category: r.Category, method: r.PaymentMethod, description: r.Description, reference: r.ReferenceID }) }
        };

        const totalToProcess = Object.values(combinedData).reduce((acc, curr) => acc + curr.length, 0);
        let processedCount = 0;

        for (const [sheetName, rows] of Object.entries(combinedData)) {
            const config = sheetMap[sheetName];
            if (!config) continue;
            for (const row of rows) {
                try {
                    const payload = config.mapper(row);
                    const res = await fetch(`${API_BASE}/${config.endpoint}`, { method: 'POST', headers, body: JSON.stringify(payload) });
                    if (res.ok) imported++;
                    else { const errData = await res.json(); if (errData.code === 'duplicate_entry') duplicates++; else errors++; }
                } catch (e) { errors++; }
                processedCount++;
                if (onProgress) onProgress(Math.round((processedCount / totalToProcess) * 100));
            }
        }
        fetchData();
        return { imported, duplicates, errors };
    };

    return (
        <SchoolContext.Provider value={{
            loading, error,
            students, leads, packages, promotions, branches, staff, instructors, settings, vehicles, payments, expenses, lessons, users, roles, pendingUsers,
            selectedBranch, setSelectedBranch,
            selectedDate, setSelectedDate,

            // Pagination
            loadMoreStudents: studentsData.loadMore, hasMoreStudents: studentsData.hasMore, totalStudentsCount: studentsData.total,
            loadMoreLeads: leadsData.loadMore, hasMoreLeads: leadsData.hasMore, totalLeadsCount: leadsData.total,
            loadMoreLessons: lessonsData.loadMore, hasMoreLessons: lessonsData.hasMore, totalLessonsCount: lessonsData.total,
            loadMorePayments: paymentsData.loadMore, hasMorePayments: paymentsData.hasMore,
            loadMoreExpenses: expensesData.loadMore, hasMoreExpenses: expensesData.hasMore,

            // CRUD
            addPackage, updatePackage, deletePackage,
            addPromotion, updatePromotion, deletePromotion,
            addBranch, updateBranch, deleteBranch,
            addStaff, updateStaff, deleteStaff,
            addInstructor, updateInstructor, deleteInstructor,
            addVehicle, updateVehicle, deleteVehicle,
            updateSetting, addUser, updateUser, approveUser, rejectUser, addLead,

            // BO Specific
            totalStudents, activePackages, monthlyIncome, runwayMonths,
            packageDistribution, statusDistribution, stageDistribution,
            financialData: generateFinancialChartData(),
            branchPerformance, lossMakingStudents,
            financials: { payments, expenses },
            handleTemplateImport, getBranchName, getBranchId,
            refetch: fetchData
        }}>
            {children}
        </SchoolContext.Provider>
    );
};
